/* eslint-disable max-len */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Steps } from 'primereact/steps';

import { Form, Formik } from 'formik';
import _ from 'lodash';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { validationMessages, notificationMessages } from '../../../i18n/common';
import ClientStep from './ClientStep';
import AssessmentStep from './AssessmentStep';
import SurveyStep from './SurveyStep';
import ReviewStep from './ReviewStep';
import {
  AccountType,
  AppConstants,
  DEFAULT_CREDIT_COMMISSION_PERCENTAGE,
  DEFAULT_NO_LIMIT_COMMISSION_PERCENTAGE,
  DEFAULT_SUPER_MATCH_CREDITS_COMMISSION_PERCENTAGE,
  MAX_CREDIT_COMMISSION_PERCENTAGE,
  MAX_NO_LIMIT_COMMISSION_PERCENTAGE,
  MINIMUM_COMMISSION_PERCENTAGE,
  MINIMUM_CREDIT_COMMISSION_PERCENTAGE,
  NumberOfEmployees,
  PayedBy,
} from '../../../shared/Constants';
import { useSelector } from 'react-redux';
import useGpMessageToOwner from '../../../hooks/useGpMessageToOwner';

const messages = defineMessages({
  panelTitleClient: { id: 'accounts.add-client.panel-header', defaultMessage: 'Add Client account invitation' },
  panelTitleCP: { id: 'accounts.add-cp.panel-header', defaultMessage: 'Add CP account invitation' },
  panelTitleMCP: { id: 'accounts.add-mcp.panel-header', defaultMessage: 'Add MCP account invitation' },
  panelTitleGSC: { id: 'accounts.add-gsc.panel-header', defaultMessage: 'Add GSC account invitation' },
  panelTitleGP: { id: 'accounts.add-gp.panel-header', defaultMessage: 'Add GP account invitation' },
  accountDetailsStepName: { id: 'accounts.add-client.steps.account-details', defaultMessage: 'Account' },
  assessmentStepName: { id: 'accounts.add-client.steps.assessment', defaultMessage: 'Assessment' },
  surveyStepName: { id: 'accounts.add-client.steps.survey', defaultMessage: 'Survey' },
  reviewStepName: { id: 'accounts.add-client.steps.review', defaultMessage: 'Review' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  next: { id: 'actions.next', defaultMessage: 'Next' },
  previous: { id: 'actions.previous', defaultMessage: 'Previous' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
});

const AddClientAccountForm = (props) => {
  const { data, type, currentAccountType, availableNumberOfEmployees, addClient } = props;

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();
  const history = useHistory();

  const { buildDefaultMessageToOwner } = useGpMessageToOwner();

  const {
    cldr: { teritoryDisplayNames },
  } = useSelector((state) => state.i18n);

  const [step, setStep] = useState(0);
  const steps = [
    { label: formatMessage(messages.accountDetailsStepName) },
    { label: formatMessage(messages.assessmentStepName) },
    { label: formatMessage(messages.surveyStepName) },
    { label: formatMessage(messages.reviewStepName) },
  ];

  const isFirstpage = step === 0;
  const isLastPage = step === 3;

  const getPanelHeader = () => {
    let panelHeader;

    if (type === AccountType.CLIENT || type === AccountType.NBM_CLIENT) {
      panelHeader = formatMessage(messages.panelTitleClient);
    } else if (type === AccountType.CP) {
      panelHeader = formatMessage(messages.panelTitleCP);
    } else if (type === AccountType.MCP) {
      panelHeader = formatMessage(messages.panelTitleMCP);
    } else if (type === AccountType.GSC) {
      panelHeader = formatMessage(messages.panelTitleGSC);
    } else if (type === AccountType.GP) {
      panelHeader = formatMessage(messages.panelTitleGP);
    }

    return panelHeader;
  };

  const extractInput = (values) => {
    const assessmentTemplateIds = values.assessmentTemplates.map(({ node }) => node.id);

    const behaviorDimensions = values.assessmentDimensions.behaviorDimensions.map((d) => d.name);
    const distortionDimensions = values.assessmentDimensions.distortionDimensions.map((d) => d.name);
    const cognitiveDimensions = values.assessmentDimensions.cognitiveDimensions.map((d) => d.name);

    const assessmentDimensions = {
      interestsDimensionsArePresent: values.assessmentDimensions.interestsDimensionsArePresent,
      behaviorDimensions,
      distortionDimensions,
      cognitiveDimensions,
    };

    const surveyAbilities = values.surveyAbilities.map((ability) => ability.id);
    const surveyTemplateIds = values.surveyTemplates.map(({ node }) => node.id);
    // GraphQL does not accept extra values in mutation input

    return {
      ...values,
      assessmentTemplates: undefined,
      assessmentTemplateIds,
      assessmentDimensions,
      surveyTemplates: undefined,
      surveyTemplateIds,
      surveyAbilities,
      type,
    };
  };

  const onHandleSubmit = async (values, formikBag) => {
    const { setSubmitting } = formikBag;

    const input = extractInput(values);

    try {
      await addClient({
        variables: {
          input,
        },
      });
      setSubmitting(false);
      growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
      history.push(`/accounts/${currentAccount}/accounts/invitations`);
    } catch (err) {
      growl.show({ severity: 'error', detail: err.message, sticky: true });
    }
  };

  const panelHeader = getPanelHeader();

  const gpiIsCreatingAnInvitationForGsc = currentAccountType === AccountType.GPI && type === AccountType.GSC;
  const gscIsCreatingAnInvitationForGP = currentAccountType === AccountType.GSC && type === AccountType.GP;
  const invitationIsForGp = type === AccountType.GP;
  const invitationIsForNbmClient = type === AccountType.NBM_CLIENT;
  const invitationIsForNewBusinessModelAccount =
    type === AccountType.NBM_CLIENT || type === AccountType.GP || type === AccountType.GSC;

  const validationSchema = Yup.object().shape({
    parentId: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    type: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    preferredLanguage: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    ownerInvoiceCountry: Yup.string().when([], {
      is: () => gpiIsCreatingAnInvitationForGsc || gscIsCreatingAnInvitationForGP,
      then: Yup.string()
        .required(formatMessage(validationMessages.required))
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
    ownerBusinessCountries: Yup.string().when([], {
      is: () => gpiIsCreatingAnInvitationForGsc,
      then: Yup.string()
        .required(formatMessage(validationMessages.required))
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
    freeTrialMonths: Yup.number().when(['payedBy'], {
      is: (payedBy) => invitationIsForNewBusinessModelAccount && payedBy === PayedBy.PAYED_BY_SELF,
      then: Yup.number(formatMessage(validationMessages.required)).nullable(),
      otherwise: Yup.number().nullable(),
    }),
    numberOfEmployees: Yup.string().when(['payedBy'], {
      is: (payedBy) => invitationIsForNbmClient && payedBy === PayedBy.PAYED_BY_SELF,
      then: Yup.string()
        .required(formatMessage(validationMessages.required))
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
    payedBy: Yup.string().when([], {
      is: () => invitationIsForNewBusinessModelAccount,
      then: Yup.string().required(formatMessage(validationMessages.required)),
      otherwise: Yup.string().nullable(),
    }),
    creditCommissionPercentage: Yup.number().when([], {
      is: () => invitationIsForGp,
      then: Yup.number()
        .required(formatMessage(validationMessages.required))
        .min(MINIMUM_CREDIT_COMMISSION_PERCENTAGE, formatMessage(validationMessages.required))
        .max(MAX_CREDIT_COMMISSION_PERCENTAGE, formatMessage(validationMessages.required)),
      otherwise: Yup.number().nullable(),
    }),
    noLimitCommissionPercentage: Yup.number().when([], {
      is: () => invitationIsForGp,
      then: Yup.number()
        .required(formatMessage(validationMessages.required))
        .min(MINIMUM_COMMISSION_PERCENTAGE, formatMessage(validationMessages.required))
        .max(MAX_NO_LIMIT_COMMISSION_PERCENTAGE, formatMessage(validationMessages.required)),
      otherwise: Yup.number().nullable(),
    }),
    email: Yup.string()
      .email(formatMessage(validationMessages.invalidEmail))
      .required(formatMessage(validationMessages.required))
      .default(''),
    fullName: Yup.string()
      .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
      .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
      .required(formatMessage(validationMessages.required))
      .default(''),
    inviteMessage: Yup.string().default(''),
    assessmentTemplates: Yup.array()
      .of(Yup.object())
      .default([]),
    assessmentDimensions: Yup.object()
      .shape({
        behaviorDimensions: Yup.array().default([]),
        distortionDimensions: Yup.array().default([]),
        cognitiveDimensions: Yup.array().default([]),
        interestsDimensionsArePresent: Yup.boolean().default(false),
      })
      .required(formatMessage(validationMessages.required)),
    surveyTemplates: Yup.array()
      .of(Yup.object())
      .default([]),
    surveyAbilities: Yup.array()
      .of(Yup.object())
      .default([]),
  });

  const initialValues = {
    parentId: currentAccount,
    type,
    payedBy: invitationIsForNewBusinessModelAccount ? PayedBy.PAYED_BY_SELF : null,
    preferredLanguage: AppConstants.DEFAULT_LOCALE,
    ownerInvoiceCountry: null,
    ownerBusinessCountries: null,
    freeTrialMonths: invitationIsForNewBusinessModelAccount ? 0 : null,
    numberOfEmployees: NumberOfEmployees.SELECTED_BY_CLIENT,
    creditCommissionPercentage: invitationIsForGp ? DEFAULT_CREDIT_COMMISSION_PERCENTAGE : null,
    noLimitCommissionPercentage: invitationIsForGp ? DEFAULT_NO_LIMIT_COMMISSION_PERCENTAGE : null,
    superCreditsCommissionPercentage: invitationIsForGp ? DEFAULT_SUPER_MATCH_CREDITS_COMMISSION_PERCENTAGE : null,
    email: '',
    fullName: '',
    inviteMessage: invitationIsForGp ? buildDefaultMessageToOwner() : '',
    assessmentTemplates: [],
    assessmentDimensions: {
      behaviorDimensions: [],
      distortionDimensions: [],
      cognitiveDimensions: [],
      interestsDimensionsArePresent: false,
    },
    surveyTemplates: [],
    surveyAbilities: [],
  };

  const { ownerBusinessCountries } = data.account;
  const { countries } = data;

  const availableCountries = countries.filter((country) => {
    if (!ownerBusinessCountries) return true;

    return ownerBusinessCountries.includes(country);
  });

  const countryOptions = teritoryDisplayNames(availableCountries);

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={panelHeader}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, formikBag) => {
                onHandleSubmit(values, formikBag);
              }}
            >
              {({ validateForm, isSubmitting, isValid }) => (
                <Form className="p-grid">
                  <div className="p-col-12">
                    <Steps model={steps} activeIndex={step} onSelect={(e) => setStep(e.index)} />
                  </div>
                  <div className="p-sm-12 p-md-6">
                    <ClientStep
                      isCurrentStep={step === 0}
                      account={data.account}
                      languages={data.activeLocales}
                      countries={countryOptions}
                      type={type}
                      availableNumberOfEmployees={availableNumberOfEmployees}
                      currentAccountType={currentAccountType}
                    />
                  </div>
                  <AssessmentStep
                    isCurrentStep={step === 1}
                    templates={data.account.assessmentTemplates}
                    sections={data.account.assessmentSections}
                  />
                  <SurveyStep
                    isCurrentStep={step === 2}
                    competencies={data.account.surveyCompetencies}
                    templates={data.account.surveyTemplates}
                  />
                  <ReviewStep
                    isCurrentStep={step === 3}
                    account={data.account}
                    sections={data.account.assessmentSections}
                  />
                  <div className="p-col-12">
                    {isFirstpage ? (
                      <Button
                        type="button"
                        label={formatMessage(messages.cancel)}
                        onClick={() => history.push(`/accounts/${currentAccount}`)}
                      />
                    ) : (
                      <Button
                        type="button"
                        label={formatMessage(messages.previous)}
                        onClick={() => setStep((stepFromState) => stepFromState - 1)}
                        disabled={!isValid}
                      />
                    )}
                    <Button
                      type="button"
                      label={formatMessage(messages.next)}
                      onClick={() => {
                        validateForm().then((validationErrors) => {
                          if (_.isEmpty(validationErrors)) {
                            setStep((stepFromState) => stepFromState + 1);
                          }
                        });
                      }}
                      disabled={!(isValid && !isLastPage)}
                    />
                    <Button
                      type="submit"
                      label={formatMessage(messages.submit)}
                      disabled={!(isValid && isLastPage) || isSubmitting}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Panel>
        </div>
      </div>
    </div>
  );
};

AddClientAccountForm.propTypes = {
  addClient: PropTypes.objectOf(PropTypes.any).isRequired,
  type: PropTypes.string.isRequired,
  currentAccountType: PropTypes.string.isRequired,
  availableNumberOfEmployees: PropTypes.arrayOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AddClientAccountForm;
